import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import './SubmitQuotation.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getCurrencySymbol = (code) => {
  const symbols = { USD: '$', EUR: '€', GBP: '£' };
  return symbols[code] || '$';
};

const createEmptyItem = () => ({ description: '', quantity: 1, unitPrice: 0, total: 0 });

const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bid, setBid] = useState(null);
  const [rfq, setRfq] = useState(null);

  const [items, setItems] = useState([]);
  const [leadTime, setLeadTime] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const [termsConditions, setTermsConditions] = useState('');
  const [notes, setNotes] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    fetchBidDetails();
  }, [id]);

  const fetchBidDetails = async () => {
    try {
      const bidRes = await fetch(`${API_BASE}/bids/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bidData = await bidRes.json();
      if (!bidData.success) throw new Error(bidData.message);

      const currentBid = bidData.bid;
      if (currentBid.status !== 'pending') {
        setError('This quotation cannot be edited as it has already been accepted or rejected.');
        setLoading(false);
        return;
      }

      setBid(currentBid);
      setCurrency(currentBid.currency || 'USD');

      const lead = currentBid.leadTime?.replace(' days', '') || '';
      setLeadTime(lead);
      setValidityDays(currentBid.validityDays?.toString() || '30');
      setTermsConditions(currentBid.termsConditions || '');
      setNotes(currentBid.technicalProposal || '');
      setSignatureName(currentBid.signatureName || '');

      const existingItems = currentBid.quotationItems || [];
      let populatedItems = [];
      if (existingItems.length === 0) {
        populatedItems = Array(4).fill().map(() => createEmptyItem());
      } else {
        populatedItems = existingItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.quantity * item.unitPrice
        }));
        const extraRows = Math.max(0, 4 - populatedItems.length);
        for (let i = 0; i < extraRows; i++) {
          populatedItems.push(createEmptyItem());
        }
      }
      setItems(populatedItems);

      if (currentBid.rfqId) {
        const rfqRes = await fetch(`${API_BASE}/rfqs/${currentBid.rfqId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rfqData = await rfqRes.json();
        if (rfqData.success) setRfq(rfqData.rfq);
      }
    } catch (err) {
      setError(err.message || 'Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => setItems([...items, createEmptyItem()]);

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unitPrice || 0);
    }
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    return { subtotal };
  };

  const { subtotal } = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = items.filter(
      item => item.description && item.description.trim() !== '' && item.quantity > 0 && item.unitPrice > 0
    );
    if (validItems.length === 0) {
      alert('Please add at least one valid quotation item with description, quantity > 0, and price > 0');
      return;
    }
    if (!leadTime) {
      alert('Please enter lead time');
      return;
    }
    if (!signatureName.trim()) {
      alert('Please enter your authorized signatory name');
      return;
    }

    setSubmitting(true);

    const payload = {
      quotationItems: validItems.map(item => ({
        description: item.description.trim(),
        quantity: parseInt(item.quantity),
        unitPrice: parseFloat(item.unitPrice),
        total: parseFloat(item.quantity) * parseFloat(item.unitPrice)
      })),
      leadTime: leadTime + ' days',
      validityDays: parseInt(validityDays),
      termsConditions: termsConditions || '',
      technicalProposal: notes || '',
      signatureName: signatureName.trim(),
      amount: subtotal,
      originalAmount: subtotal,
      commissionAmount: 0,
      currency: currency
    };

    try {
      const res = await fetch(`${API_BASE}/bids/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        alert('Quotation updated successfully!');
        navigate('/service-provider/my-offers');
      } else {
        setError(data.message || 'Update failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-spinner">Loading quotation...</div>;
  if (error) return (
    <div className="error-message" style={{ textAlign: 'center', padding: '40px' }}>
      {error}<br/>
      <button onClick={() => navigate(-1)} style={{ marginTop: '16px' }}>Go Back</button>
    </div>
  );
  if (!bid || !rfq) return <div className="error-message">Quotation not found</div>;

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <div className="submit-quotation-page">
      <button className="back-btn" onClick={() => navigate(-1)}>
        <i className="fas fa-arrow-left"></i> Back
      </button>

      <div className="page-header">
        <h1><i className="fas fa-edit"></i> Edit Quotation</h1>
        <p>Update your quotation details. Changes will be visible to the ship owner.</p>
      </div>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="quotation-card">
          <h3><i className="fas fa-info-circle"></i> Job Details</h3>
          <div className="job-info-grid">
            <div className="job-info-item">
              <span className="label">Vessel</span>
              <span className="value">{rfq.vesselName}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Service Type</span>
              <span className="value">{rfq.serviceType}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Current Port</span>
              <span className="value">{rfq.port}</span>
            </div>
            <div className="job-info-item">
              <span className="label">Currency</span>
              <span className="value">{currency}</span>
            </div>
          </div>
        </div>

        <div className="quotation-card">
          <div className="items-header">
            <h3><i className="fas fa-list-ul"></i> Quotation Items</h3>
            <Button type="button" variant="outline" size="small" onClick={addItem} icon="fa-plus">
              Add Item
            </Button>
          </div>

          <div className="items-table">
            <div className="items-table-header">
              <span>Description</span>
              <span>Quantity</span>
              <span>Unit Price ({currency})</span>
              <span className="col-total">Total ({currency})</span>
              <span className="col-action"></span>
            </div>

            {items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="col-desc">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="Enter item description..."
                  />
                </div>
                <div className="col-qty">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>
                <div className="col-price">
                  <input
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-total">
                  <span className="item-total">
                    {currencySymbol}{(item.quantity * item.unitPrice).toLocaleString()}
                  </span>
                </div>
                <div className="col-action">
                  <button
                    type="button"
                    className="remove-item-btn"
                    onClick={() => removeItem(index)}
                    disabled={items.length <= 1}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="totals-section">
            <div className="section-title">
              <i className="fas fa-calculator"></i> Cost Breakdown ({currency})
            </div>
            <div className="total-row">
              <span>Total Quotation:</span>
              <strong>{currencySymbol}{subtotal.toLocaleString()}</strong>
            </div>
            <div className="total-row you-receive">
              <span>✅ You Will Receive:</span>
              <strong>{currencySymbol}{subtotal.toLocaleString()}</strong>
            </div>
          </div>
        </div>

        <div className="quotation-card">
          <h3><i className="fas fa-cog"></i> Quotation Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Lead Time (Days) *</label>
              <input
                type="number"
                value={leadTime}
                onChange={(e) => setLeadTime(e.target.value)}
                min="1"
                required
                placeholder="e.g., 5"
              />
            </div>
            <div className="form-group">
              <label>Validity Period</label>
              <select value={validityDays} onChange={(e) => setValidityDays(e.target.value)}>
                <option value="15">15 Days</option>
                <option value="30">30 Days</option>
                <option value="45">45 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Terms & Conditions</label>
            <textarea
              rows="3"
              value={termsConditions}
              onChange={(e) => setTermsConditions(e.target.value)}
              placeholder="Enter terms and conditions..."
            />
          </div>
          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes or technical details..."
            />
          </div>
        </div>

        <div className="quotation-card">
          <h3><i className="fas fa-signature"></i> Authorized Signatory</h3>
          <div className="form-group">
            <label>Full Name (as authorized signatory) *</label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="e.g., Capt. John Smith, Managing Director"
              required
            />
          </div>
        </div>

        <div className="form-actions">
          <Button variant="secondary" onClick={() => navigate(-1)} type="button">Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Updating...' : 'Update Quotation'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditQuotation;